import SpaceBetween from '@risk-smart/themed-cloudscape-components/space-between';
import Button from '@risksmart-app/components/src/button';
import { PageNotFound } from '@risksmart-app/components/src/errors/errors';
import { useGetGuidParam } from '@risksmart-app/components/src/routes/routes.utils';
import type { IssueTaxonomyKeys } from '@risksmart-app/shared/forms/issues/types';
import { Parent_Type_Enum } from '@risksmart-app/web-graphql-client/generated/graphql';
import type { FC } from 'react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import type { ObjectToolbarConfig } from 'src/blocks/object-toolbar';
import { ObjectToolbar } from 'src/blocks/object-toolbar';
import { useObjectToolbarFormStore } from 'src/blocks/object-toolbar/useObjectToolbarFormStore';
import ControlledTabs from 'src/components/controlled-tabs';
import TabSettingsModal from 'src/components/tab-settings-modal';
import { PageLayout } from 'src/layouts';
import { Permission } from 'src/rbac/Permission';
import { useHasPermissionQuery } from 'src/rbac/useHasPermission';
import {
  useGetDetailParentPath,
  useGetDetailPath,
} from 'src/routes/useGetDetailParentPath';

import { useDeleteControls } from '@/hooks/mutations';
import { useGetControlById } from '@/hooks/queries';
import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';
import { useIsModuleEnabled } from '@/hooks/useIsModuleEnabled';
import { useDeleteResultNotification } from '@/hooks/useMutationResultNotification';
import useTabs from '@/hooks/useTabs';
import { getFriendlyId } from '@/utils/friendlyId';

import DeleteControlModal from '../tab/delete-modal/DeleteControlModal';
import useExporter from './useExporter';

type Props = {
  activeTabId:
    | 'actions'
    | 'approvals'
    | 'details'
    | 'indicators'
    | 'issues'
    | 'linkedItems'
    | 'notificationHistory'
    | 'performance'
    | IssueTaxonomyKeys;
  showDeleteButton?: boolean;
};

const Page: FC<Props> = ({ activeTabId, showDeleteButton }) => {
  const controlId = useGetGuidParam('controlId');

  const navigate = useNavigate();

  const detailPath = useGetDetailPath(controlId);
  const parentPath = useGetDetailParentPath(controlId);

  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
  const [showTabSettings, setShowTabSettings] = useState(false);
  const { t } = useTranslation(['common']);
  const { t: st } = useTranslation(['common'], { keyPrefix: 'controls' });
  const defaultTitle = st('fallback_title');
  const objectToolbarEnabled = useIsFeatureFlagEnabled('object_toolbar');
  // Form-editor opener bridged up from the active tab's FormMenu (relocate mode).
  // Present only on the details tab when object_toolbar + form_sections are on.
  const formConfig = useObjectToolbarFormStore((s) => s.formConfigState);

  const { data, error } = useGetControlById({
    queryArgs: { controlId },
  });
  if (error) {
    throw error;
  }
  if (data?.control.length === 0) {
    throw new PageNotFound(`Control with id ${controlId} not found`);
  }
  const control = data?.control?.[0];
  const oversightEnabled = useIsModuleEnabled(
    'assessment.subModules.oversight_register_and_findings'
  );
  const internalAuditEnabled = useIsModuleEnabled('internal_audit_entity');
  const {
    hasPermission: canViewCompliance,
    loading: canViewComplianceLoading,
  } = useHasPermissionQuery('read:oversight_assessment', control);
  const {
    hasPermission: canViewInternalAudit,
    loading: canViewInternalAuditLoading,
  } = useHasPermissionQuery('read:internal_audit_report', control);
  const [exportItem, { loading: exporting }] = useExporter(
    controlId,
    internalAuditEnabled &&
      canViewInternalAudit &&
      !canViewInternalAuditLoading,
    oversightEnabled && canViewCompliance && !canViewComplianceLoading
  );
  const tabs = useTabs({
    parentType: Parent_Type_Enum.Control,
    parent: control,
    hrefRoot: detailPath,
  });

  const { deleteControls, loading: deleteLoading } = useDeleteControls();

  const onDelete = useDeleteResultNotification({
    asyncAction: async () => {
      // Delete by the ROUTE id, not `control.Id` — see the same change on the
      // indicator detail page (ENG-616). `control` is undefined until
      // `useGetControlById` resolves while the Delete button is already on
      // screen, so guarding on it makes a confirm clicked inside the read window
      // a silent no-op with an unhandled `Error: Control not selected`.
      await deleteControls([controlId]);
      navigate(parentPath);
      setIsDeleteModalVisible(false);

      return true;
    },
    entityName: st('entity_name'),
    failureAction: () => {
      setIsDeleteModalVisible(false);
    },
  });
  const friendlyId =
    control && getFriendlyId(Parent_Type_Enum.Control, control.SequentialId);
  const counter = friendlyId && `(${friendlyId})`;

  // Object-level toolbar (CUS-153) — flag-gated. Relocates Export + Delete into
  // the toolbar's overflow ⋮ menu, hosts the "Edit tabs" (and, when the active
  // tab publishes one, "Edit form") action, and surfaces the Save disc / Cancel X
  // (save state bridged from the active tab's form via useObjectToolbarFormStore).
  const toolbarConfig: ObjectToolbarConfig = useMemo(
    () => ({
      header: {
        title: control?.Title ?? defaultTitle,
        counter: friendlyId || undefined,
      },
      parentObject: control,
      save: true,
      actions: {
        overflow: [
          {
            id: 'edit',
            label: t('edit'),
            actions: [
              ...(formConfig
                ? [
                    {
                      id: 'edit-form',
                      label: t('edit_form'),
                      iconName: 'file-06' as const,
                      onClick: formConfig.openConfigure,
                    },
                  ]
                : []),
              {
                id: 'edit-tabs',
                label: t('edit_tabs'),
                iconName: 'layers-three-01',
                onClick: () => setShowTabSettings(true),
              },
            ],
          },
        ],
        export: [
          {
            id: 'export',
            actions: [
              {
                id: 'export',
                label: t('export.standard_pdf'),
                iconName: 'download-01',
                onClick: exportItem,
                disabled: exporting,
              },
            ],
          },
        ],
        // Delete stays gated by the route-level `showDeleteButton` flag (parity
        // with the legacy button); the toolbar additionally resolves the
        // `delete:control` permission instance-level from `parentObject`.
        ...(showDeleteButton
          ? {
              danger: [
                {
                  id: 'delete',
                  label: t('delete'),
                  iconName: 'trash-01' as const,
                  permission: 'delete:control' as const,
                  onClick: () => setIsDeleteModalVisible(true),
                },
              ],
            }
          : {}),
      },
    }),
    [
      control,
      defaultTitle,
      friendlyId,
      t,
      exportItem,
      exporting,
      formConfig,
      showDeleteButton,
    ]
  );

  return (
    <PageLayout
      title={control?.Title}
      meta={{
        title: defaultTitle,
      }}
      counter={counter}
      headerOverride={
        objectToolbarEnabled ? (
          <ObjectToolbar config={toolbarConfig} />
        ) : undefined
      }
      actions={
        <SpaceBetween direction={'horizontal'} size={'xs'}>
          <Button
            iconName={'download'}
            disabled={exporting}
            onClick={exportItem}
          >
            {t('export.export')}
          </Button>
          {showDeleteButton && (
            <Permission permission={'delete:control'} parentObject={control}>
              <Button
                variant={'normal'}
                formAction={'none'}
                onClick={() => {
                  setIsDeleteModalVisible(true);
                }}
              >
                {t('delete')}
              </Button>
            </Permission>
          )}
        </SpaceBetween>
      }
    >
      <ControlledTabs
        activeTabId={activeTabId}
        tabs={tabs}
        variant={'container'}
        parentType={Parent_Type_Enum.Control}
        parent={control}
        disableSettings={objectToolbarEnabled}
      />
      <TabSettingsModal
        parentType={Parent_Type_Enum.Control}
        isVisible={showTabSettings}
        onDismiss={() => setShowTabSettings(false)}
        parent={control}
      />
      <DeleteControlModal
        loading={deleteLoading}
        isVisible={isDeleteModalVisible}
        onDelete={onDelete}
        onDismiss={() => setIsDeleteModalVisible(false)}
        showUnlink={false}
      />
    </PageLayout>
  );
};

export default Page;
